import { useState, useEffect, useCallback } from 'react'
import { Button } from '../../Common/Button'
import { AppDialog } from '../../Common/AppDialog'
import { useNls } from '../../../nls/nlsProvider'
import { getApiErrorMessage } from '../../../services/apiConnectivity'
import { getHosPatientSettings } from '../../../services/hosPatientsService'
import { setHosPatientLegalSetup } from '../../../services/ownerConsoleService'

export interface HosPatientLegalSetupCardProps {
  workspaceId: string
}

export function HosPatientLegalSetupCard({ workspaceId }: HosPatientLegalSetupCardProps) {
  const { t } = useNls()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmed, setConfirmed] = useState<boolean | null>(null)
  const [confirmedAt, setConfirmedAt] = useState<string | null>(null)
  const [confirmedBy, setConfirmedBy] = useState<string | null>(null)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [isRevokeOpen, setIsRevokeOpen] = useState(false)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  const loadStatus = useCallback(async () => {
    if (!workspaceId) return
    setLoading(true)
    setError(null)
    try {
      const cfg = await getHosPatientSettings({ workspaceId })
      setConfirmed(Boolean(cfg?.legalSetupConfirmed))
      setConfirmedAt(cfg?.legalSetupConfirmedAt ?? null)
      setConfirmedBy(cfg?.legalSetupConfirmedBy ?? null)
    } catch {
      // Initial mount check failure defaults to unconfirmed without surfacing a screen-wide alert
      setConfirmed(false)
    } finally {
      setLoading(false)
    }
  }, [workspaceId])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- async fetch on mount
    void loadStatus()
  }, [loadStatus])

  const handleConfirmLegalSetup = async () => {
    setIsConfirmOpen(false)
    setLoading(true)
    setError(null)
    setSuccessMsg(null)
    try {
      await setHosPatientLegalSetup({
        workspaceId,
        confirmed: true,
      })
      setConfirmed(true)
      setSuccessMsg(t('NLSOwnerConsoleHosPatientLegalSuccess'))
      await loadStatus()
    } catch (err) {
      setError(getApiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const handleRevokeLegalSetup = async () => {
    setIsRevokeOpen(false)
    setLoading(true)
    setError(null)
    setSuccessMsg(null)
    try {
      await setHosPatientLegalSetup({
        workspaceId,
        confirmed: false,
      })
      setConfirmed(false)
      setSuccessMsg(t('NLSOwnerConsoleHosPatientLegalSuccess'))
      await loadStatus()
    } catch (err) {
      setError(getApiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <article
      className="ownerCard ownerOpsRepairCard"
      aria-labelledby="hos-patient-legal-setup-heading"
      data-testid="hos-patient-legal-setup-card"
    >
      <div className="ownerCardHeader">
        <div>
          <h3 id="hos-patient-legal-setup-heading">
            {t('NLSOwnerConsoleHosPatientLegalSetupTitle')}
          </h3>
          <p>{t('NLSOwnerConsoleHosPatientLegalSetupDesc')}</p>
          <div className="ownerCardStatus">
            <strong>
              {confirmed
                ? t('NLSOwnerConsoleHosPatientLegalStatusConfirmed')
                : t('NLSOwnerConsoleHosPatientLegalStatusNotConfirmed')}
            </strong>
            {confirmed && confirmedBy && (
              <span className="ownerSubtext"> ({confirmedBy}{confirmedAt ? ` · ${new Date(confirmedAt).toLocaleDateString()}` : ''})</span>
            )}
          </div>
        </div>
        <div className="ownerCardActions">
          {!confirmed ? (
            <Button
              variant="primary"
              disabled={loading}
              onClick={() => setIsConfirmOpen(true)}
            >
              {t('NLSOwnerConsoleHosPatientLegalConfirmBtn')}
            </Button>
          ) : (
            <Button
              variant="secondary"
              disabled={loading}
              onClick={() => setIsRevokeOpen(true)}
            >
              {t('NLSOwnerConsoleHosPatientLegalRevokeBtn')}
            </Button>
          )}
        </div>
      </div>

      {error ? (
        <p className="ownerError">
          {error}
        </p>
      ) : null}

      {successMsg ? (
        <p className="ownerSuccess" role="status">
          {successMsg}
        </p>
      ) : null}

      {isConfirmOpen ? (
        <AppDialog
          open={isConfirmOpen}
          onDismiss={() => setIsConfirmOpen(false)}
          title={t('NLSOwnerConsoleHosPatientLegalConfirmDialogTitle')}
          message={<p>{t('NLSOwnerConsoleHosPatientLegalConfirmDialogBody')}</p>}
          actions={[
            {
              label: t('NLSOwnerConsoleHosPatientLegalCancelBtn'),
              variant: 'ghost',
              onClick: () => setIsConfirmOpen(false),
            },
            {
              label: t('NLSOwnerConsoleHosPatientLegalConfirmBtn'),
              variant: 'primary',
              onClick: handleConfirmLegalSetup,
            },
          ]}
        />
      ) : null}

      {isRevokeOpen ? (
        <AppDialog
          open={isRevokeOpen}
          onDismiss={() => setIsRevokeOpen(false)}
          title={t('NLSOwnerConsoleHosPatientLegalRevokeDialogTitle')}
          message={<p>{t('NLSOwnerConsoleHosPatientLegalRevokeDialogBody')}</p>}
          actions={[
            {
              label: t('NLSOwnerConsoleHosPatientLegalCancelBtn'),
              variant: 'ghost',
              onClick: () => setIsRevokeOpen(false),
            },
            {
              label: t('NLSOwnerConsoleHosPatientLegalRevokeBtn'),
              variant: 'danger',
              onClick: handleRevokeLegalSetup,
            },
          ]}
        />
      ) : null}
    </article>
  )
}
